import { invitationPairingCodeSchema } from "@msys/common";
import { z } from "zod";

export const REMEMBERED_PAIRING_CODE_KEY =
  "msys.invitationPairing.rememberedCode";
const FRESHNESS_MS = 10 * 60 * 1000;

const storedSchema = z.object({
  code: invitationPairingCodeSchema,
  savedAt: z.number(),
});

/** Remembers a scanned pairing code. Returns false if the code is invalid or storage is unavailable. */
export function rememberPairingCode(
  code: string,
  now: number = Date.now()
): boolean {
  const parsed = invitationPairingCodeSchema.safeParse(code);
  if (!parsed.success) return false;
  try {
    localStorage.setItem(
      REMEMBERED_PAIRING_CODE_KEY,
      JSON.stringify({ code: parsed.data, savedAt: now })
    );
    return true;
  } catch {
    return false;
  }
}

/** Returns the remembered code if saved less than 10 minutes ago, otherwise null. */
export function readRememberedPairingCode(
  now: number = Date.now()
): string | null {
  try {
    const raw = localStorage.getItem(REMEMBERED_PAIRING_CODE_KEY);
    if (raw === null) return null;
    const stored = storedSchema.safeParse(JSON.parse(raw));
    if (!stored.success) return null;
    if (now - stored.data.savedAt >= FRESHNESS_MS) return null;
    return stored.data.code;
  } catch {
    return null;
  }
}

/** Forgets the remembered code, e.g. once its pairing has been completed. */
export function forgetRememberedPairingCode(): void {
  try {
    localStorage.removeItem(REMEMBERED_PAIRING_CODE_KEY);
  } catch {
    // Storage unavailable: nothing was remembered either.
  }
}
